import { PATHWAY } from "@/constants/testIds";
import {
  GOVERNANCE_FACETS,
  KATLAS_DEFINITION,
  KATLAS_GOVERNS,
  WORKFLOW,
} from "@/lib/pathwayData";
import { SectionEyebrow } from "@/components/pathway/Journey";

export default function WorkflowDiagram() {
  return (
    <section
      id="workflow"
      className="border-t hairline bg-[#0f0f0f]"
    >
      <div className="mx-auto max-w-7xl px-6 py-24 md:px-12 md:py-32">
        <div className="grid gap-8 md:grid-cols-[1.2fr_1fr] md:items-end md:gap-16">
          <div>
            <SectionEyebrow>
              Runtime governance · operational placement
            </SectionEyebrow>
            <h2 className="mt-4 font-serif text-3xl leading-tight tracking-tight text-neutral-50 sm:text-4xl lg:text-5xl">
              Where KATLAS sits in the underwriting workflow.
            </h2>
          </div>
          <p className="text-neutral-400">
            Every workflow passes through an operational governance node.
            AI may recommend; KATLAS governs; people remain accountable.
            This is the difference between <em>AI makes decisions</em> and
            <em> AI recommendations are governed</em>.
          </p>
        </div>

        {/* Top half — vertical underwriting flow with KATLAS node in the middle */}
        <div
          data-testid={PATHWAY.workflowDiagram}
          className="mt-14 grid gap-0 border hairline md:grid-cols-[1fr_2fr_1fr]"
        >
          {/* Left rail — upstream actors */}
          <div className="border-b hairline md:border-b-0 md:border-r">
            <RailHeading>Upstream</RailHeading>
            <RailNodes
              items={WORKFLOW.pre}
              testIdFn={(i) => PATHWAY.workflowPre(i)}
            />
          </div>

          {/* Centre — KATLAS Runtime Governance node */}
          <div
            data-testid={PATHWAY.workflowKatlas}
            className="border-b hairline bg-[#0a0a0a] md:border-b-0"
          >
            <RailHeading tone="katlas">KATLAS runtime governance</RailHeading>
            <div className="border-t border-b hairline p-6 md:p-8">
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                {GOVERNANCE_FACETS.map((f) => (
                  <div
                    key={f.slug}
                    data-testid={PATHWAY.workflowFacet(f.slug)}
                    className="border hairline px-4 py-3"
                  >
                    <div className="font-mono text-[10px] uppercase tracking-[0.24em] text-amber-500">
                      {f.label}
                    </div>
                    <div className="mt-1 text-[12px] leading-snug text-neutral-300">
                      {f.blurb}
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="px-6 py-4 font-mono text-[10px] uppercase tracking-[0.22em] text-neutral-500 md:px-8">
              {KATLAS_DEFINITION}
            </div>
          </div>

          {/* Right rail — downstream consequences */}
          <div className="md:border-l hairline">
            <RailHeading>Downstream</RailHeading>
            <RailNodes
              items={WORKFLOW.post}
              testIdFn={(i) => PATHWAY.workflowPost(i)}
            />
          </div>
        </div>

        {/* What KATLAS governs — universal visual */}
        <div
          data-testid={PATHWAY.governsDiagram}
          className="mt-12 border hairline"
        >
          <div className="border-b hairline px-6 py-4 font-mono text-[10px] uppercase tracking-[0.28em] text-neutral-500 md:px-8">
            What KATLAS governs
          </div>
          <div className="grid grid-cols-1 md:grid-cols-[1fr_2fr] md:items-stretch">
            <div className="border-b hairline p-6 md:border-b-0 md:border-r md:p-8">
              <div className="font-mono text-[10px] uppercase tracking-[0.24em] text-neutral-500">
                Upstream
              </div>
              <ul className="mt-4 space-y-2">
                {KATLAS_GOVERNS.upstream.map((u, i) => (
                  <li
                    key={u}
                    data-testid={PATHWAY.governsUpstream(i + 1)}
                    className="font-serif text-xl text-neutral-100"
                  >
                    {u}
                  </li>
                ))}
              </ul>
              <div className="mt-6 font-mono text-[10px] uppercase tracking-[0.22em] text-amber-500">
                ↓ pass through KATLAS
              </div>
            </div>
            <div className="p-6 md:p-8">
              <div className="font-mono text-[10px] uppercase tracking-[0.24em] text-neutral-500">
                Downstream
              </div>
              <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3">
                {KATLAS_GOVERNS.downstream.map((d, i) => (
                  <div
                    key={d}
                    data-testid={PATHWAY.governsDownstream(i + 1)}
                    className="border hairline px-3 py-3 font-serif text-lg text-neutral-100"
                  >
                    {d}
                  </div>
                ))}
              </div>
              <div className="mt-6 font-mono text-[10px] uppercase tracking-[0.22em] text-neutral-500">
                Everything passes through the same runtime.
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function RailHeading({ children, tone }) {
  return (
    <div
      className={`px-6 py-4 font-mono text-[10px] uppercase tracking-[0.28em] md:px-8 ${
        tone === "katlas" ? "text-amber-500" : "text-neutral-500"
      }`}
    >
      {children}
    </div>
  );
}

function RailNodes({ items, testIdFn }) {
  return (
    <ol className="border-t hairline">
      {items.map((it, i) => (
        <li
          key={it}
          data-testid={testIdFn(i + 1)}
          className="border-b hairline px-6 py-5 md:px-8"
        >
          <div className="font-mono text-[10px] tracking-[0.24em] text-neutral-500">
            {String(i + 1).padStart(2, "0")}
          </div>
          <div className="mt-1 font-serif text-xl text-neutral-100">{it}</div>
        </li>
      ))}
    </ol>
  );
}
